import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { AppShell } from '@/components/templates/app-shell'
import { EntityForm } from '@/components/templates/entity-form'
import { EmptyState } from '@/components/ui/empty-state'
import {
  PlatformAssociationFormFields,
  associationTimeZones,
  type CreateAssociationForm,
} from '@/pages/PlatformAssociationFormFields'
import { ApiError, getJSON, postJSON } from '@/lib/api'
import type {
  PlatformAssociation,
  PlatformAssociationsResponse,
} from '@/pages/PlatformAssociationsListPage'
import { platformAdminNav } from '@/pages/platformAdminNav'
import type { PlatformSurface } from '@/pages/PlatformAdminSignInPage'

interface PlatformCreateAssociationPageProps {
  platform: PlatformSurface
  unauthorizedFallback?: React.ReactNode
  onCancel?: () => void
  onCreated?: (association: PlatformAssociation) => void
}

interface AssociationResponse {
  association: PlatformAssociation
}

type ValidationErrors = Record<string, string[]>

export default function PlatformCreateAssociationPage({
  platform,
  unauthorizedFallback,
  onCancel,
  onCreated,
}: PlatformCreateAssociationPageProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [form, setForm] = React.useState<CreateAssociationForm>({
    profile: 'none',
    name: '',
    subdomain: '',
    timeZone: associationTimeZones[0],
  })
  const accessQuery = useQuery({
    queryKey: ['platform-associations'],
    queryFn: () => getJSON<PlatformAssociationsResponse>('/api/v1/platform/associations'),
    retry: false,
  })
  const createAssociation = useMutation({
    mutationFn: () =>
      postJSON<AssociationResponse>(
        '/api/v1/platform/associations',
        form.profile === 'fab'
          ? {
              demo_profile: 'fab',
              web_address_name: form.subdomain,
            }
          : {
              name: form.name,
              web_address_name: form.subdomain,
              time_zone: form.timeZone,
            },
      ),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['platform-associations'] })
      if (onCreated) {
        onCreated(data.association)
      } else {
        navigate(`/platform/associations/${data.association.id}`)
      }
    },
  })
  const errors = validationErrors(createAssociation.error)

  if (
    accessQuery.error instanceof ApiError &&
    accessQuery.error.status === 401 &&
    unauthorizedFallback
  ) {
    return unauthorizedFallback
  }

  return (
    <AppShell
      associationName={platform.brand}
      nav={platformAdminNav}
      user={{ name: 'Platform Admin' }}
    >
      {accessQuery.isLoading && <EmptyState title="Loading create Association" />}
      {accessQuery.isError && !accessQuery.isLoading && (
        <EmptyState title="Create Association unavailable" description="Refresh and try again." />
      )}
      {accessQuery.isSuccess && (
        <EntityForm
          title="New association"
          className="mx-0 px-6 py-6"
          titleClassName="text-subheading font-semibold"
          submitLabel={form.profile === 'fab' ? 'Create demo association' : 'Create association'}
          error={formError(createAssociation.error, errors)}
          submitting={createAssociation.isPending}
          onCancel={onCancel}
          onSubmit={(event) => {
            event.preventDefault()
            createAssociation.mutate()
          }}
        >
          <PlatformAssociationFormFields
            form={form}
            onChange={(nextForm) => {
              if (nextForm.profile !== form.profile) createAssociation.reset()
              setForm(nextForm)
            }}
            errors={errors}
          />
        </EntityForm>
      )}
    </AppShell>
  )
}

function validationErrors(error: Error | null): ValidationErrors {
  if (!(error instanceof ApiError) || !hasValidationErrors(error.body)) return {}

  return error.body.errors
}

function hasValidationErrors(body: unknown): body is { errors: ValidationErrors } {
  if (typeof body !== 'object' || body === null || !('errors' in body)) return false

  const errors = (body as { errors: unknown }).errors
  return typeof errors === 'object' && errors !== null
}

function formError(error: Error | null, errors: ValidationErrors) {
  if (!error || Object.keys(errors).length > 0) return undefined

  return error.message
}
